
import { PropsWithChildren, useRef, useState, useEffect, ReactNode } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import classNames from "classnames";
import { XMarkIcon } from '@heroicons/react/24/outline';

interface DragState {
    isDragging: boolean;
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
}

interface ResizeState {
    isResizing: boolean;
    startX: number;
    startY: number;
    initialWidth: number;
    initialHeight: number;
    direction: 'se' | 'e' | 's' | 'sw' | 'w' | 'nw' | 'n' | 'ne' | null;
}

export interface ModalAction {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
    disabled?: boolean;
}

export default function Modal({
                                  children,
                                  show = false,
                                  maxWidth = '2xl',
                                  className = '',
                                  closeable = true,
                                  onClose = () => {},
                                  showOverlay = true,
                                  draggable = true,
                                  dragHandle,
                                  initialPosition,
                                  resizable = true,
                                  initialSize,
                                  minWidth = 300,
                                  minHeight = 200,
                                  autoResize = true,
                                  title,
                                  actions,
                              }: PropsWithChildren<{
    show: boolean;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'empty';
    className?: string;
    closeable?: boolean;
    onClose: CallableFunction;
    showOverlay?: boolean;
    draggable?: boolean;
    dragHandle?: string;
    initialPosition?: { x: number; y: number };
    resizable?: boolean;
    initialSize?: { width: number; height: number };
    minWidth?: number;
    minHeight?: number;
    autoResize?: boolean;
    title?: ReactNode;
    actions?: ModalAction[];
}>) {
    const modalRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState(initialPosition || { x: 0, y: 0 });
    const [size, setSize] = useState(initialSize || { width: 0, height: 0 });
    const [autoResizedWidth, setAutoResizedWidth] = useState<number | null>(null);

    const [dragState, setDragState] = useState<DragState>({
        isDragging: false,
        startX: 0,
        startY: 0,
        initialX: 0,
        initialY: 0,
    });

    const [resizeState, setResizeState] = useState<ResizeState>({
        isResizing: false,
        startX: 0,
        startY: 0,
        initialWidth: 0,
        initialHeight: 0,
        direction: null,
    });

    const close = () => {
        if (closeable) {
            onClose();
        }
    };

    // Get viewport constraints
    const getViewportConstraints = () => {
        const padding = 20; // Minimum padding from viewport edges
        return {
            minX: padding,
            minY: padding,
            maxX: window.innerWidth - padding,
            maxY: window.innerHeight - padding,
            maxWidth: window.innerWidth - (padding * 2),
            maxHeight: window.innerHeight - (padding * 2),
        };
    };

    // Constrain position and size to viewport
    const constrainToViewport = (newPos: { x: number; y: number }, newSize: { width: number; height: number }) => {
        const constraints = getViewportConstraints();

        // Ensure minimum size
        const constrainedSize = {
            width: Math.max(minWidth, Math.min(newSize.width || minWidth, constraints.maxWidth)),
            height: Math.max(minHeight, Math.min(newSize.height || minHeight, constraints.maxHeight)),
        };

        // Constrain position to keep modal within viewport
        const constrainedPos = {
            x: Math.max(constraints.minX, Math.min(newPos.x, constraints.maxX - constrainedSize.width)),
            y: Math.max(constraints.minY, Math.min(newPos.y, constraints.maxY - constrainedSize.height)),
        };

        return { position: constrainedPos, size: constrainedSize };
    };

    const maxWidthClass = {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-xl',
        '2xl': 'sm:max-w-2xl',
        '3xl': 'sm:max-w-3xl',
        '4xl': 'sm:max-w-4xl',
        'empty': '',
    }[maxWidth];

    const shouldStartDrag = (target: HTMLElement): boolean => {
        if (!draggable) return false;

        if (target.classList.contains('resize-handle')) return false;

        if (dragHandle) {
            return !!target.closest(dragHandle);
        } else {
            const interactiveElements = ['INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'A'];
            const tagName = target.tagName.toUpperCase();

            if (interactiveElements.includes(tagName)) return false;

            if (target.contentEditable === 'true') return false;

            if (target.onclick || target.getAttribute('onclick')) return false;

            const styles = window.getComputedStyle(target);
            if (styles.cursor === 'pointer') return false;

            return true;
        }
    };

    const getResizeDirection = (target: HTMLElement): ResizeState['direction'] => {
        if (!target.classList.contains('resize-handle')) return null;

        if (target.classList.contains('resize-se')) return 'se';
        if (target.classList.contains('resize-e')) return 'e';
        if (target.classList.contains('resize-s')) return 's';
        if (target.classList.contains('resize-sw')) return 'sw';
        if (target.classList.contains('resize-w')) return 'w';
        if (target.classList.contains('resize-nw')) return 'nw';
        if (target.classList.contains('resize-n')) return 'n';
        if (target.classList.contains('resize-ne')) return 'ne';

        return null;
    };

    const checkAndAutoResize = () => {
        if (!autoResize || !modalRef.current) return;

        const modal = modalRef.current;
        const constraints = getViewportConstraints();
        const modalRect = modal.getBoundingClientRect();

        if (modalRect.height > constraints.maxHeight) {
            const currentWidth = size.width || modalRect.width;
            const newWidth = Math.min(currentWidth * 1.2, constraints.maxWidth);

            const constrained = constrainToViewport(position, { width: newWidth, height: constraints.maxHeight });
            setAutoResizedWidth(newWidth);
            setSize(constrained.size);
            setPosition(constrained.position);
        } else if (autoResizedWidth) {
            setAutoResizedWidth(null);
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;

        const resizeDirection = getResizeDirection(target);
        if (resizable && resizeDirection) {
            e.preventDefault();
            e.stopPropagation();

            const rect = modalRef.current?.getBoundingClientRect();
            if (!rect) return;

            setResizeState({
                isResizing: true,
                startX: e.clientX,
                startY: e.clientY,
                initialWidth: size.width || rect.width,
                initialHeight: size.height || rect.height,
                direction: resizeDirection,
            });
            return;
        }

        if (!shouldStartDrag(target)) return;

        e.preventDefault();
        e.stopPropagation();

        const rect = modalRef.current?.getBoundingClientRect();
        if (!rect) return;

        setDragState({
            isDragging: true,
            startX: e.clientX,
            startY: e.clientY,
            initialX: position.x,
            initialY: position.y,
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (resizeState.isResizing && resizable) {
            e.preventDefault();

            const deltaX = e.clientX - resizeState.startX;
            const deltaY = e.clientY - resizeState.startY;

            let newWidth = resizeState.initialWidth;
            let newHeight = resizeState.initialHeight;
            let newX = position.x;
            let newY = position.y;

            const direction = resizeState.direction!;

            if (direction.includes('e')) {
                newWidth = Math.max(minWidth, resizeState.initialWidth + deltaX);
            } else if (direction.includes('w')) {
                newWidth = Math.max(minWidth, resizeState.initialWidth - deltaX);
                newX = position.x + (resizeState.initialWidth - newWidth);
            }

            if (direction.includes('s')) {
                newHeight = Math.max(minHeight, resizeState.initialHeight + deltaY);
            } else if (direction.includes('n')) {
                newHeight = Math.max(minHeight, resizeState.initialHeight - deltaY);
                newY = position.y + (resizeState.initialHeight - newHeight);
            }

            const constrained = constrainToViewport({ x: newX, y: newY }, { width: newWidth, height: newHeight });
            setSize(constrained.size);
            setPosition(constrained.position);
            setAutoResizedWidth(null);
        } else if (dragState.isDragging && draggable) {
            e.preventDefault();

            const deltaX = e.clientX - dragState.startX;
            const deltaY = e.clientY - dragState.startY;

            const newPos = {
                x: dragState.initialX + deltaX,
                y: dragState.initialY + deltaY,
            };

            const constrained = constrainToViewport(newPos, size);
            setPosition(constrained.position);
        }
    };

    const handleMouseUp = () => {
        if (resizeState.isResizing) {
            setResizeState(prev => ({ ...prev, isResizing: false, direction: null }));
        }
        if (dragState.isDragging) {
            setDragState(prev => ({ ...prev, isDragging: false }));
        }
    };

    // Handle viewport resize
    useEffect(() => {
        const handleResize = () => {
            if (show) {
                const constrained = constrainToViewport(position, size);
                if (constrained.position.x !== position.x || constrained.position.y !== position.y) {
                    setPosition(constrained.position);
                }
                // A size of zero means the modal is fitting its content, which a window resize leaves alone.
                // Clamping it would store the minimum size and collapse the modal to it.
                const sized = size.width > 0 || size.height > 0;
                if (sized && (constrained.size.width !== size.width || constrained.size.height !== size.height)) {
                    setSize(constrained.size);
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [show, position, size]);

    useEffect(() => {
        if (dragState.isDragging || resizeState.isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
        };
    }, [dragState.isDragging, resizeState.isResizing]);

    useEffect(() => {
        if (show) {
            const timeoutId = setTimeout(checkAndAutoResize, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [show, children]);

    useEffect(() => {
        if (show && !initialPosition) {
            setPosition({ x: 0, y: 0 });
        }
        if (show && !initialSize) {
            setSize({ width: 0, height: 0 });
            setAutoResizedWidth(null);
        }
    }, [show, initialPosition, initialSize]);

    const ResizeHandles = () => {
        if (!resizable) return null;

        const handles = [
            { direction: 'nw', className: 'top-0 left-0 cursor-nw-resize' },
            { direction: 'n', className: 'top-0 left-1/2 transform -translate-x-1/2 cursor-n-resize' },
            { direction: 'ne', className: 'top-0 right-0 cursor-ne-resize' },
            { direction: 'e', className: 'top-1/2 right-0 transform -translate-y-1/2 cursor-e-resize' },
            { direction: 'se', className: 'bottom-0 right-0 cursor-se-resize' },
            { direction: 's', className: 'bottom-0 left-1/2 transform -translate-x-1/2 cursor-s-resize' },
            { direction: 'sw', className: 'bottom-0 left-0 cursor-sw-resize' },
            { direction: 'w', className: 'top-1/2 left-0 transform -translate-y-1/2 cursor-w-resize' },
        ];

        return (
            <>
                {handles.map(({ direction, className }) => (
                    <div
                        key={direction}
                        className={classNames(
                            'resize-handle absolute w-3 h-3 bg-transparent hover:bg-blue-500 hover:bg-opacity-50 z-20',
                            `resize-${direction}`,
                            className
                        )}
                    />
                ))}
            </>
        );
    };

    const getButtonClassName = (variant: ModalAction['variant'] = 'secondary') => {
        const baseClasses = 'px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

        switch (variant) {
            case 'primary':
                return `${baseClasses} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`;
            case 'danger':
                return `${baseClasses} bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed`;
            default:
                return `${baseClasses} bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-600 dark:text-white dark:hover:bg-gray-700`;
        }
    };

    const constraints = getViewportConstraints();

    // Helper function to get maxWidth value from prop
    const getMaxWidthValue = () => {
        const maxWidthClasses = {
            sm: 384, // 24rem
            md: 448, // 28rem
            lg: 512, // 32rem
            xl: 576, // 36rem
            '2xl': 672, // 42rem
            '3xl': 768, // 48rem
            '4xl': 896, // 56rem
            'empty': constraints.maxWidth,
        };
        return maxWidthClasses[maxWidth] || constraints.maxWidth;
    };

    const propMaxWidth = getMaxWidthValue();

    const modalStyle = {
        transform: `translate(${showOverlay ? position.x : `calc(-50% + ${position.x}`}px, ${showOverlay ? position.y : `calc(-50% + ${position.y}`}px)`,
        transition: (dragState.isDragging || resizeState.isResizing) ? 'none' : 'transform 0.3s ease-out',
        cursor: dragState.isDragging ? 'grabbing' : resizeState.isResizing ? 'grabbing' : 'default',
        width: size.width > 0 ? `${Math.min(size.width, Math.min(constraints.maxWidth, propMaxWidth))}px` : undefined,
        height: size.height > 0 ? `${Math.min(size.height, constraints.maxHeight)}px` : undefined,
        maxWidth: size.width > 0 ? `${Math.min(constraints.maxWidth, propMaxWidth)}px` : `${Math.min(constraints.maxWidth, propMaxWidth)}px`,
        maxHeight: `${constraints.maxHeight}px`,
        minWidth: `${minWidth}px`,
        minHeight: `${minHeight}px`,
    };

    const ModalHeader = () => {
        if (!title && !closeable) return null;

        return (
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white modal-header flex-1">
                    {title}
                </div>
                {closeable && (
                    <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-lg p-1.5"
                        onClick={close}
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                )}
            </div>
        );
    };

    const ModalFooter = () => {
        if (!actions?.length) return null;

        return (
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
                {actions.map((action, index) => (
                    <button
                        key={index}
                        type="button"
                        className={getButtonClassName(action.variant)}
                        onClick={action.onClick}
                        disabled={action.disabled}
                    >
                        {action.label}
                    </button>
                ))}
            </div>
        );
    };

    if (!showOverlay) {
        return (
            <Transition show={show} leave="duration-200">
                <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        ref={modalRef}
                        className={classNames(
                            `fixed bg-background dark:bg-background-dark rounded-lg shadow-xl transform transition-all ${size.width > 0 ? '' : maxWidthClass}`,
                            (dragState.isDragging || resizeState.isResizing) && 'select-none',
                            'relative overflow-hidden flex flex-col',
                            className
                        )}
                        style={{
                            zIndex: 50,
                            left: '50%',
                            top: '50%',
                            ...modalStyle
                        }}
                        onMouseDown={handleMouseDown}
                    >
                        <ResizeHandles />
                        <ModalHeader />
                        <div className="relative z-10 flex-1 overflow-auto p-6" style={{ cursor: 'default' }}>
                            {children}
                        </div>
                        <ModalFooter />
                    </div>
                </TransitionChild>
            </Transition>
        );
    }

    return (
        <Transition show={show} leave="duration-200">
            <Dialog
                as="div"
                id="modal"
                className="fixed inset-0 flex overflow-y-auto px-4 py-6 sm:px-0 items-center transform transition-all"
                style={{
                    zIndex: 99999
                }}
                onClose={close}
            >
                <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="absolute inset-0 bg-gray-500/75" />
                </TransitionChild>

                <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <DialogPanel
                        ref={modalRef}
                        className={classNames(
                            `mb-6 bg-background dark:bg-background-dark rounded-lg shadow-xl transform transition-all sm:w-full sm:mx-auto ${size.width > 0 ? '' : maxWidthClass}`,
                            (dragState.isDragging || resizeState.isResizing) && 'select-none',
                            'relative overflow-hidden flex flex-col',
                            className
                        )}
                        style={modalStyle}
                        onMouseDown={handleMouseDown}
                    >
                        <ResizeHandles />
                        <ModalHeader />
                        <div className="relative z-10 flex-1 overflow-auto p-6" style={{ cursor: 'default' }}>
                            {children}
                        </div>
                        <ModalFooter />
                    </DialogPanel>
                </TransitionChild>
            </Dialog>
        </Transition>
    );
}
